/**
 * Presentation helpers for the workbench. Colour discipline follows the
 * queue's: red only for genuine trouble (a contradiction), amber for what
 * needs someone, everything else quiet.
 */

import type { ChecklistStatus, EvidenceStatus } from '@/types/workbench';

interface Chip {
  label: string;
  color: string;
}

export const CHECKLIST_STATUS: Record<ChecklistStatus, Chip> = {
  open: { label: 'Open', color: 'gray' },
  in_progress: { label: 'In progress', color: 'blue' },
  done: { label: 'Done', color: 'teal' },
  not_possible: { label: 'Not possible', color: 'gray' },
  waived: { label: 'Waived', color: 'gray' },
};

export const EVIDENCE_STATUS: Record<EvidenceStatus, Chip> = {
  claimed: { label: 'Claimed', color: 'gray' },
  corroborated: { label: 'Corroborated', color: 'teal' },
  contradicted: { label: 'Contradicted', color: 'red' },
  discounted: { label: 'Discounted', color: 'gray' },
  unverifiable: { label: 'Unverifiable', color: 'orange' },
};

/** Corroborated first, then claimed; what was set aside last. */
export const EVIDENCE_RANK: Record<EvidenceStatus, number> = {
  corroborated: 0,
  claimed: 1,
  contradicted: 2,
  unverifiable: 3,
  discounted: 4,
};

const SOURCE_LABELS: Record<string, string> = {
  provider: 'Screening',
  capture: 'Web capture',
  np_return: "Partner's return",
  client_document: 'Client file',
  order_sheet: 'Order sheet',
  analyst_note: 'Analyst note',
  identification: 'Identification',
};

export function sourceLabel(sourceType: string): string {
  return SOURCE_LABELS[sourceType] ?? words(sourceType);
}

const FILE_KINDS: Record<string, string> = {
  client_file: 'Client file',
  order_sheet: 'Order sheet',
  partner_return: "Partner's return",
  provider_file: "Provider's file",
  partner_form: "Partner's form",
  other: 'File',
};

export function fileKindLabel(kind: string | null): string {
  return kind ? (FILE_KINDS[kind] ?? words(kind)) : 'File';
}

/** `official_record` → "official record". */
export function words(value: string | null | undefined): string {
  return (value ?? '').replace(/_/g, ' ');
}

/** "$0.0084" under a dollar (agent runs are cents), "$12.40" above. */
export function formatUsd(amount: string | number | null | undefined): string | null {
  if (amount === null || amount === undefined || amount === '') {
    return null;
  }
  const value = Number(amount);
  if (!Number.isFinite(value)) {
    return null;
  }
  return `$${value < 1 ? value.toFixed(4) : value.toFixed(2)}`;
}

/** "2.4 KB", "1.2 MB". */
export function formatBytes(size: number | null): string | null {
  if (size === null || !Number.isFinite(size)) {
    return null;
  }
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}
